import { ReactNode, useEffect, useMemo, useRef } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

import { useQuery } from '@apollo/client';

import {
  BillingInterval,
  GetViewerAvailableProgramPlansDocument,
  GetViewerAvailableProgramsDocument,
} from '@sandtech-hub-monorepo/ehub-client';
import { cn } from '@sandtech-hub-monorepo/ui-kit';
import {
  planParams,
  trackEvent,
  AnalyticsEvent,
} from '@sandtech-hub-monorepo/ui-auth-kit';

import { AppRoutesPaths } from '../../routes/types';
import { useEnrollInProgram } from '../../data/useEnrollInProgram';
import { useProgramCoverage } from '../../data/useProgramCoverage';
import { useGsaEnrollments } from '../../data/gsaEnrollments';
import {
  FREE_ANALYTICS_PLAN,
  toAnalyticsPlan,
} from '../../utils/analyticsPlan';
import { FreePlanCard } from './components/FreePlanCard';
import { PlanCard, PurchasablePlan } from './components/PlanCard';
import { paymentTermsButtonLabels } from '../../utils/partnerTermPlan';

/**
 * Not on the marketing site yet, so served from this app's own build instead
 * of linking out — see apps/set-edu/project.json's `assets` entry, which
 * copies apps/set-edu/src/assets/legal/payment-terms.pdf onto the deployed
 * site unchanged. Move to a frontiertech.edu URL once marketing publishes it.
 */
const PAYMENT_TERMS_URL = '/assets/legal/payment-terms.pdf';

/*
 * Tailwind matches whole class names in the source, so the layouts are written
 * out per column count rather than built from the number of plans. The count
 * includes the Free card.
 */
const GRID_BY_COLUMNS: Record<number, string> = {
  1: 'max-w-md',
  2: 'max-w-3xl sm:grid-cols-2',
  3: 'max-w-5xl md:grid-cols-2 lg:grid-cols-3',
  4: 'max-w-6xl md:grid-cols-2 xl:grid-cols-4',
};

/**
 * The plans page, served at `/subscribe`.
 *
 * It never names a program. A subscription is bought as a subscription — it
 * unlocks every program its plan covers — so putting one program's name on the
 * page would describe a scoping rule as if it were the product.
 *
 * The program id has not gone away, it has just stopped being the learner's
 * problem. `createStripeCheckoutSession` requires one and refuses a plan that
 * does not cover it, so every plan below carries an id taken from its own
 * coverage: `?programId=` when the learner arrived from a program page, and
 * otherwise the first program the plan actually unlocks. Covered by
 * construction rather than by guesswork — which is what the old fallback to
 * "the first GSA program the server returned" was.
 *
 * Nothing here mentions a trial. The backend sets no `trial_period_days`, so
 * the first charge is immediate and in full; copy promising otherwise would be
 * contradicted by the Stripe page one click later.
 */
export const Plans = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const requestedProgramId = searchParams.get('programId');
  const { enroll, enrolling } = useEnrollInProgram();

  /*
   * Whether this visit is about starting a particular program. Scoped entries
   * come from a program's Start button; a cold entry comes from the
   * subscription banner or Settings, where there is no program in play.
   *
   * It matters because the two ends of the picker enrol only when scoped. Note
   * that `checkoutProgramId` below cannot stand in for this: on a cold entry
   * it is derived from a plan's own coverage to satisfy the checkout mutation,
   * so it names a program the learner never chose.
   */
  const startingProgramId = requestedProgramId;

  /*
   * One round trip for the whole picture: every program open to the learner,
   * and which plans unlock each. It returns degree programs too — the query
   * takes no arguments — so the GSA filter is applied below. The page needs
   * the inverse of that mapping, how many programs a plan covers, and no field
   * answers it, so it is inverted here.
   */
  const { data, loading } = useQuery(GetViewerAvailableProgramPlansDocument, {
    fetchPolicy: 'cache-first',
  });

  /*
   * The two halves of "is there anything here for this learner".
   *
   * They are separate questions and both have to be asked. Enrolment says the
   * program has already been started; coverage says a live subscription
   * already unlocks it. Neither implies the other - a plan bought for one
   * program covers every program it lists, started or not - so only the pair
   * means there is nothing left to sell AND nothing left to start.
   *
   * Enrolled but UNCOVERED must stay on this page. That is the free-tier
   * learner coming to upgrade, and this picker is where the detail page's
   * "Subscribe to All Access" and the enrolled card's upsell both send them.
   *
   * The enrolment read is the same cache-first document `useEnrollInProgram`
   * already queries above, so Apollo answers it without a second request; it
   * is read here directly because that hook exposes neither the flag nor its
   * loading state.
   */
  const { data: programsData, loading: programsLoading } = useQuery(
    GetViewerAvailableProgramsDocument,
    { fetchPolicy: 'cache-first' }
  );
  const { covered, loading: coverageLoading } =
    useProgramCoverage(requestedProgramId);

  /*
   * Free is only the learner's CURRENT plan if no live subscription covers
   * any of their enrollments — otherwise they are already paying, and the
   * badge would be lying. Checked across every enrollment rather than the
   * scoped program: a subscriber upgrading from one program's page still has
   * an active plan, just not one that covers this particular program yet.
   */
  const { current: gsaEnrollments, loading: enrollmentsLoading } =
    useGsaEnrollments();
  const isOnFreePlan =
    !enrollmentsLoading && !gsaEnrollments.some((e) => e.covered);

  const startingProgram = (programsData?.viewer?.availablePrograms ?? []).find(
    (program) => program.id === startingProgramId
  );
  const alreadyEnrolled = !!startingProgram?.isEnrolled;

  /*
   * `covered` is false until the first answer lands, so redirecting before
   * both reads settle would never fire for a learner who has paid - and
   * rendering the grid meanwhile would flash it at someone about to be sent
   * away. Only scoped visits wait: `useProgramCoverage(null)` skips its query
   * and reports settled, so a cold entry from the banner or Settings is
   * unaffected.
   */
  const guardPending =
    !!requestedProgramId && (programsLoading || coverageLoading);

  /*
   * Covered but not yet started: nothing to sell, only something to start, so
   * enrol instead of showing a grid whose obvious button is "Subscribe to All
   * Access" (IR-2518). The catalogue and detail page already enrol covered
   * programs directly; this catches anything else that lands here. A covered
   * program the learner cannot start still gets the grid, as before.
   */
  const startsCovered =
    !guardPending &&
    !!startingProgram &&
    !alreadyEnrolled &&
    startingProgram.canEnroll &&
    covered;

  // Once per visit: a refused enrolment must not retry on every render.
  const coveredEnrollAttempted = useRef(false);

  useEffect(() => {
    if (!startsCovered || coveredEnrollAttempted.current) return;

    coveredEnrollAttempted.current = true;
    enroll(startingProgram.id, { included: true });
    // `enroll` is a fresh closure every render; keying on it would re-fire.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startsCovered]);

  const plans = useMemo<PurchasablePlan[]>(() => {
    const gsaPrograms = (data?.viewer?.availablePrograms ?? []).filter(
      (program) => !program.isUniversityProgram
    );

    const scoped = requestedProgramId
      ? gsaPrograms.filter((program) => program.id === requestedProgramId)
      : gsaPrograms;

    const byPlanId = new Map<string, PurchasablePlan>();

    for (const program of scoped) {
      for (const plan of program.billingPlans) {
        const existing = byPlanId.get(plan.id);

        if (existing) {
          existing.programCount += 1;
          continue;
        }

        byPlanId.set(plan.id, {
          ...plan,
          programCount: 1,
          checkoutProgramId: program.id,
        });
      }
    }

    // Counting only the scoped programs would say "1 program" on every plan
    // reached from a program page, so the count is recomputed over the whole
    // catalogue. What a plan covers does not depend on how you arrived.
    if (requestedProgramId) {
      for (const plan of byPlanId.values()) {
        plan.programCount = gsaPrograms.filter((program) =>
          program.billingPlans.some((candidate) => candidate.id === plan.id)
        ).length;
      }
    }

    return [...byPlanId.values()];
  }, [data, requestedProgramId]);

  const goToCheckout = (plan: PurchasablePlan, interval: BillingInterval) => {
    trackEvent(
      AnalyticsEvent.SubscriptionPlanSelected,
      planParams(toAnalyticsPlan(plan, interval), startingProgram)
    );

    const params = new URLSearchParams({
      programId: plan.checkoutProgramId,
      planId: plan.id,
      interval,
    });

    // Carried through checkout to the confirmation page, which enrols on a
    // confirmed session. Absent on a cold entry, where there is no program the
    // learner asked to start.
    if (startingProgramId) params.set('enroll', '1');

    navigate(`${AppRoutesPaths.SubscribeCheckout}?${params}`);
  };

  /*
   * Nothing to sell and nothing to start, so the picker has no question left
   * to ask - send them to the program they are already working through.
   * Placed after every hook, as hooks must be, and after `guardPending` so it
   * cannot fire on a default `false`.
   *
   * Safe to point at /learning: `isEnrolled` is resolved against ONGOING
   * enrollments only, which is the same filter that page lists by, so the
   * program is there when they arrive.
   */
  if (
    !guardPending &&
    requestedProgramId &&
    alreadyEnrolled &&
    covered &&
    // Our own enrolment's refetch lands here before its onCompleted, which
    // opens the program; don't bounce through /learning first.
    !coveredEnrollAttempted.current
  ) {
    return <Navigate to={AppRoutesPaths.Learning} replace />;
  }

  // The covered enrolment was refused (its toast says why). The grid would
  // offer to sell what they already hold, so send them to the program page,
  // which knows they are covered.
  if (startsCovered && coveredEnrollAttempted.current && !enrolling) {
    return (
      <Navigate
        to={AppRoutesPaths.Program.replace(
          ':programId',
          startingProgram.abbreviation
        )}
        replace
      />
    );
  }

  let body: ReactNode;

  if (
    startsCovered ||
    coveredEnrollAttempted.current ||
    guardPending ||
    (loading && !data)
  ) {
    // Deliberately not ui-kit-site's `Loading`, which is h-screen/w-screen and
    // would open a viewport-sized hole under the heading it sits below.
    body = (
      <div className="mt-12 flex flex-col items-center gap-3 text-center">
        <Loader2 className="h-7 w-7 animate-spin text-primary" />
        <p className="text-muted-foreground">Loading plans…</p>
      </div>
    );
  } else if (plans.length === 0) {
    // An empty list is a real answer — no active plan covers anything the
    // learner can reach — so it is said out loud rather than rendered as a grid
    // with only the Free card in it.
    body = (
      <div className="mt-12 text-center">
        <h2 className="text-xl font-semibold text-heading">
          No plans available yet
        </h2>
        <p className="mt-2 text-muted-foreground">
          There is nothing on sale right now. Free modules stay open without a
          subscription, so you can keep going and come back.
        </p>
        <Link
          to={AppRoutesPaths.Programs}
          className="mt-4 inline-block font-medium text-primary"
        >
          Back to programs
        </Link>
      </div>
    );
  } else {
    body = (
      <div
        className={cn(
          'mx-auto mt-12 grid items-stretch gap-6',
          GRID_BY_COLUMNS[plans.length + 1] ?? GRID_BY_COLUMNS[4]
        )}
      >
        {/*
          Staying free is a way of starting the program, not a way of declining
          — so when the learner came here to start one, this is what enrols
          them. Reached cold there is nothing to enrol in, and it just goes
          back where they came from.
        */}
        {/*
          All-access ranks above free on mobile (product's call), but the grid
          keeps free first from `sm` up, where columns already sit side by
          side and the reading order stops mattering.
        */}
        <FreePlanCard
          className="order-3 sm:order-1"
          busy={enrolling}
          isCurrentPlan={isOnFreePlan}
          onStayFree={() => {
            // Reached cold, "Stay on Free" is just going back, not a choice.
            if (!startingProgramId) return navigate(-1);
            trackEvent(
              AnalyticsEvent.SubscriptionPlanSelected,
              planParams(FREE_ANALYTICS_PLAN, startingProgram)
            );
            enroll(startingProgramId, { trial: true });
          }}
        />
        {plans.map((plan) => (
          <PlanCard
            key={plan.id}
            className="order-1 sm:order-2"
            plan={plan}
            onSelect={(interval) => goToCheckout(plan, interval)}
          />
        ))}
        {/*
          Placed as its own grid item rather than nested under the card, so it
          cannot inflate the row height the cards stretch to match — explicit
          placement under the paid card's column keeps it there on `sm` and up,
          `order-2` keeps it directly beneath the paid card (order-1) on mobile,
          ahead of Free (order-3).
        */}
        <p className="order-2 text-center text-sm text-muted-foreground sm:col-start-2 sm:row-start-2">
          {/* Quotes the NFBPA Early Enrollment button too when it is shown
              (PEDS-17209). Delete it together with partnerTermPlan.ts. */}
          By selecting{' '}
          {paymentTermsButtonLabels(plans.map((plan) => plan.slug))}, you agree
          to Frontier&apos;s{' '}
          <Link
            to={PAYMENT_TERMS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary"
          >
            Payment Terms.
          </Link>
        </p>
      </div>
    );
  }

  return (
    <div className="bg-background px-4 py-16">
      <div className="mx-auto max-w-6xl">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-heading">
            Choose Your Access
          </h1>
          <p className="mt-2 text-muted-foreground">
            Select the plan that works best for you
          </p>
        </div>
        {body}
      </div>
    </div>
  );
};
